import { getJson, patchJson, postJson, requestVoid } from './client';

// --- Types (partner VendorCouponController) ---

export type CouponDiscountType = 'FIXED' | 'RATE';
export type CouponScopeType = 'ALL_ROOMS' | 'SPECIFIC_ROOMS';
export type CouponValidityType = 'FIXED_PERIOD' | 'RELATIVE_DAYS';
export type CouponStatus = 'ACTIVE' | 'PAUSED' | 'EXHAUSTED' | 'EXPIRED';

export type CreateVendorCouponRequest = {
  title: string;
  discountType: CouponDiscountType;
  discountValue: number;
  maxDiscountWon?: number | null;
  minPurchaseWon: number;
  scopeType: CouponScopeType;
  scopeRoomIds?: string[];
  issueStartAt: string;
  issueEndAt: string;
  validityType: CouponValidityType;
  validFrom?: string | null;
  validUntil?: string | null;
  validDaysAfterClaim?: number | null;
  totalIssueLimit?: number | null;
  totalBudgetWon?: number | null;
  perUserLimit: number;
};

export type UpdateVendorCouponRequest = {
  title?: string;
  issueEndAt?: string;
  totalIssueLimit?: number;
  totalBudgetWon?: number;
};

export type VendorCouponDetail = {
  id: string;
  code: string | null;
  title: string;
  discountType: CouponDiscountType;
  discountValue: number;
  maxDiscountWon: number | null;
  minPurchaseWon: number;
  issuerType: string;
  issuerId: string | null;
  scopeType: CouponScopeType;
  scopeRoomIds: string[];
  issueStartAt: string | null;
  issueEndAt: string | null;
  validityType: CouponValidityType;
  validFrom: string | null;
  validUntil: string | null;
  validDaysAfterClaim: number | null;
  totalIssueLimit: number | null;
  totalBudgetWon: number | null;
  perUserLimit: number;
  status: CouponStatus | string;
  issuedCount: number;
  totalDiscountUsedWon: number;
  pausedAt: string | null;
  pausedBy: string | null;
  pausedReason: string | null;
  createdBy: string | null;
  createdAt: string;
};

export type VendorCouponListResponse = {
  items: VendorCouponDetail[];
  totalCount: number;
  page: number;
  size: number;
};

// --- Functions ---

export function createVendorCoupon(req: CreateVendorCouponRequest) {
  return postJson<VendorCouponDetail>('/api/v1/vendors/me/coupons', req);
}

export function getVendorCoupons(
  params: { status?: CouponStatus | string; page?: number; size?: number } = {},
) {
  const query = new URLSearchParams();
  if (params.status) query.set('status', params.status);
  if (params.page != null) query.set('page', String(params.page));
  if (params.size != null) query.set('size', String(params.size));
  const qs = query.toString();
  return getJson<VendorCouponListResponse>(`/api/v1/vendors/me/coupons${qs ? `?${qs}` : ''}`);
}

export function getVendorCoupon(couponId: string) {
  return getJson<VendorCouponDetail>(`/api/v1/vendors/me/coupons/${encodeURIComponent(couponId)}`);
}

export function updateVendorCoupon(couponId: string, req: UpdateVendorCouponRequest) {
  return patchJson<VendorCouponDetail>(
    `/api/v1/vendors/me/coupons/${encodeURIComponent(couponId)}`,
    req,
  );
}

export function pauseVendorCoupon(couponId: string, reason: string) {
  return requestVoid(`/api/v1/vendors/me/coupons/${encodeURIComponent(couponId)}/pause`, {
    body: JSON.stringify({ reason }),
    method: 'PATCH',
  });
}

export function resumeVendorCoupon(couponId: string) {
  return requestVoid(`/api/v1/vendors/me/coupons/${encodeURIComponent(couponId)}/resume`, {
    method: 'PATCH',
  });
}
